import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, CheckCircle2, MinusCircle, ArrowRightLeft, MessageSquare, RotateCw, Bookmark } from "lucide-react";
import { OriginBadge } from "../../lib/origin.jsx";
import { useRole } from "../../lib/role.jsx";
import { useCopilot } from "../../lib/copilot.jsx";
import { getExplanation, streamExplanation } from "../../lib/classificationApi";
import { cn } from "../../lib/utils";

const MODEL_LABEL = { "claude-sonnet-4-5-20250929": "Claude Sonnet 4.5" };
const clean = (t) => t.replace(/\*\*/g, "").replace(/[\s*_`]*WHAT WOULD CHANGE:[\s*_`]*/i, "\n\nWhat would change: ");
const when = (iso) => new Date(iso).toLocaleString("en-PH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function RulesFired({ rules, testid }) {
  return (
    <div className="mt-3" data-testid={`${testid}-rules`}>
      <div className="text-[9.5px] uppercase tracking-widest text-muted font-semibold mb-1.5">Rules that fired · and those that didn't</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border rounded-sm overflow-hidden">
        {rules.map(r => (
          <div key={r.id} data-testid={`${testid}-rule-${r.id}`} data-fired={r.fired ? "yes" : "no"}
               className={cn("bg-bg px-2.5 py-2 flex items-start gap-2 text-[11px] leading-snug", !r.fired && "opacity-70")}>
            {r.fired ? <CheckCircle2 size={12} className="text-success mt-0.5 shrink-0" /> : <MinusCircle size={12} className="text-subtle mt-0.5 shrink-0" />}
            <div className="min-w-0"><span className="font-medium">{r.title}</span><span className="text-muted"> — {r.detail}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Written({ exp, testid }) {
  return (
    <div data-testid={`${testid}-text`} className="animate-fade-in">
      <div className="space-y-2 text-[12.5px] leading-relaxed text-fg/95">
        {exp.text.split(/\n\s*\n/).map((p, i) => <p key={i}>{p.trim()}</p>)}
      </div>
      {exp.change && (
        <div data-testid={`${testid}-change`} className="mt-2.5 flex items-start gap-2 border-l-2 border-warning bg-warning/[0.06] px-2.5 py-2 text-[12px]">
          <ArrowRightLeft size={12} className="text-warning mt-0.5 shrink-0" />
          <span><span className="font-semibold">What would change the class: </span>{exp.change}</span>
        </div>
      )}
    </div>
  );
}

function useExplanation(kind, id, auto, branch) {
  const { user } = useRole();
  const userRef = useRef(user);
  userRef.current = user;
  const ctrl = useRef(null);
  const [s, setS] = useState({ status: "loading", meta: null, exp: null, live: "", error: null });

  const generate = useCallback(async () => {
    ctrl.current?.abort();
    const c = new AbortController();
    ctrl.current = c;
    setS(p => ({ ...p, status: "streaming", live: "", error: null }));
    try {
      await streamExplanation(kind, id, userRef.current, {
        signal: c.signal, branch,
        onMeta: meta => setS(p => ({ ...p, meta })),
        onToken: t => setS(p => ({ ...p, live: p.live + t })),
        onDone: exp => setS(p => ({ ...p, exp, status: "ready" })),
      });
    } catch (e) {
      if (e.name !== "AbortError") setS(p => ({ ...p, status: "error", error: e.message }));
    }
  }, [kind, id, branch]);

  useEffect(() => {
    let alive = true;
    setS({ status: "loading", meta: null, exp: null, live: "", error: null });
    getExplanation(kind, id, branch)
      .then(d => {
        if (!alive) return;
        setS(p => ({ ...p, meta: d, exp: d.explanation, status: d.explanation ? "ready" : "idle" }));
        if (!d.explanation && auto) generate();
      })
      .catch(e => alive && setS(p => ({ ...p, status: "error", error: e.message })));
    return () => { alive = false; ctrl.current?.abort(); };
  }, [kind, id, auto, generate]);
  return { ...s, generate };
}

// Copilot's plain-language reason for a SKU's (or a cell's) class, grounded in pipeline figures and saved per run inputs.
export default function ClassExplanation({ kind, id, branch, auto = false, title = "Why this class · Copilot", className, testid,
                                          idleHint = "Copilot explains this SKU's class from its own pipeline figures. Saved for this run.",
                                          generateLabel = "Explain this class" }) {
  const { askCopilot } = useCopilot();
  const { status, meta, exp, live, error, generate } = useExplanation(kind, id, auto, branch);
  const followUp = () => askCopilot(
    `Follow-up on the class explanation for ${meta.label}: what does this mean for how we stock it, and what should I watch next?`,
    { ...meta.copilot_context, lines: [...meta.copilot_context.lines, `Explanation shown to the user: ${exp.text.slice(0, 1400)}`] },
  );
  return (
    <div data-testid={testid} className={className}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-primary inline-flex items-center gap-1.5"><Sparkles size={11} />{title}</span>
        {meta && <OriginBadge {...meta.origin} testid={`${testid}-origin`} />}
      </div>
      {status === "loading" && <div className="text-[12px] text-muted py-2"><Loader2 size={12} className="inline animate-spin mr-1.5" />Checking for a saved explanation…</div>}
      {status === "idle" && (
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid={`${testid}-generate`} onClick={generate}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-medium bg-primary text-primary-fg rounded-sm hover:opacity-90 active:scale-[0.98] transition-[opacity,transform]">
            <Sparkles size={12} /> {generateLabel}
          </button>
          <span className="text-[11px] text-muted">{idleHint}</span>
        </div>
      )}
      {status === "streaming" && (
        <p data-testid={`${testid}-streaming`} className="text-[12.5px] leading-relaxed text-fg/95 whitespace-pre-line">
          {live ? clean(live) : <span className="text-muted"><Loader2 size={12} className="inline animate-spin mr-1.5" />Copilot is reading the pipeline figures…</span>}
          <span className="inline-block w-1.5 h-3.5 bg-primary ml-0.5 align-middle animate-pulse" />
        </p>
      )}
      {status === "ready" && exp && <Written exp={exp} testid={testid} />}
      {status === "error" && (
        <div data-testid={`${testid}-error`} className="flex flex-wrap items-center gap-3 text-[12px] text-critical">
          {error}
          <button data-testid={`${testid}-retry`} onClick={generate} className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"><RotateCw size={11} /> Try again</button>
        </div>
      )}
      {meta?.rules && status !== "loading" && <RulesFired rules={meta.rules} testid={testid} />}
      {status === "ready" && exp && (
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 text-[10.5px] text-subtle">
          <span data-testid={`${testid}-meta`} className="inline-flex items-center gap-1 flex-wrap">
            <Bookmark size={10} />{exp.cached ? "Saved" : "New"} · {MODEL_LABEL[exp.model] || exp.model} · written {when(exp.generated_at)} in run <span className="font-mono-tabular">{exp.run_id}</span>
            {meta?.run_id && meta.run_id !== exp.run_id && <> · inputs unchanged, still valid for current run <span className="font-mono-tabular">{meta.run_id}</span></>}
          </span>
          <button data-testid={`${testid}-ask-copilot`} onClick={followUp} className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium">
            <MessageSquare size={11} /> Ask Copilot a follow-up →
          </button>
        </div>
      )}
    </div>
  );
}
